/**
 * The actions on Your Plan, on the right with the main action last (stacked full width on phones, main action first):
 * update the plan, which starts at the consumer flow's first step, inside the flow, with every saved answer kept
 * (nothing is reset); book the complimentary planning session (hidden once self-funding access has expired); then
 * download the report (disabled until the plan's figures are ready and nothing is saving).
 * EDJ pilot advisors get the report emailed instead, which the confirmation dialog explains.
 */
import { CalendarDays } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@design-system';
import { DownloadReportWithPdfOptions } from '@/features/client-onboarding';
import { EdjPdfEmailSentDialog } from '@/features/pdf-report';
import { useReportSectionOptions } from '../hooks/use-report-section-options';
import { consumerStepPath } from '../lib/paths';
import { LTC_PLANNING_SESSION_LABEL, LTC_PLANNING_SESSION_URL } from '../lib/planning-session';
import { FIRST_CONSUMER_STEP } from '../lib/steps';

interface YourPlanActionsProps {
  clientId: string;
  report: {
    download: () => void;
    canDownload: boolean;
    isDownloading: boolean;
    emailSent: { isOpen: boolean; advisorEmail?: string };
    dismissEmailSent: () => void;
  };
  showPlanningSession: boolean;
  /** The report is insurance-only while self-funding access is expired. */
  isAccessExpired?: boolean;
}

export function YourPlanActions({
  clientId,
  report,
  showPlanningSession,
  isAccessExpired = false,
}: YourPlanActionsProps) {
  useReportSectionOptions();

  return (
    <div
      className="flex flex-col-reverse gap-3 sm:flex-row sm:items-start sm:justify-end"
      data-testid="your-plan-actions"
    >
      <Button
        asChild
        variant="link"
        className="max-sm:w-full"
      >
        <Link to={consumerStepPath(clientId, FIRST_CONSUMER_STEP)}>Update your plan</Link>
      </Button>
      {showPlanningSession && (
        <Button
          asChild
          variant="outline"
          className="max-sm:w-full"
        >
          <a
            href={LTC_PLANNING_SESSION_URL}
            target="_blank"
            rel="noreferrer"
          >
            <CalendarDays />
            {LTC_PLANNING_SESSION_LABEL}
          </a>
        </Button>
      )}
      <div className="flex flex-col gap-1 max-sm:w-full sm:items-end">
        <DownloadReportWithPdfOptions
          onDownload={report.download}
          loading={report.isDownloading}
          disabled={!report.canDownload}
          label="Download your report"
          loadingLabel="Preparing your report…"
          className="max-sm:w-full sm:w-auto"
        />
        {isAccessExpired && (
          <p className="text-muted-foreground text-xs">
            Self-funding isn’t included while your access is expired.
          </p>
        )}
      </div>
      <EdjPdfEmailSentDialog
        open={report.emailSent.isOpen}
        onOpenChange={open => {
          if (!open) report.dismissEmailSent();
        }}
        advisorEmail={report.emailSent.advisorEmail}
      />
    </div>
  );
}
